import React, { useState, useEffect, useCallback, useRef } from "react";
import useApi from "@/hooks/useApi";
import useNetSuiteConfigs from "@/hooks/useNetSuiteConfigs";
import SearchBar from "@/components/SearchBar";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { IconPlus } from "@velaro/velaro-component-lib/";
import { IconCheck } from "@velaro/velaro-component-lib/";
import NetsuitePanel from "./NetSuitePanel";
import { getMetaDataOptions } from "./MetaDataOptions";

const handleSearch = (searchText: string) => {};

export interface NetSuiteMap {
  id: number;
  netSuiteField: any;
  label: string;
  linkedTo: string;
  create: boolean;
  usedForSearching: boolean;
  showWhenSelected: boolean;
  showInSearch: boolean;
  control: string;
  defaultValue: string;
  recordType: string;
}

interface Props {
  teamId: number;
  selectedConfigId: number;
}

export default function NetSuiteDataMapping(props: Props) {
  const [panels, setPanels] = useState<{ id: number; metadataType: string }[]>(
    []
  );
  const [showSaved, setShowSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mappedFields, setMappedFields] = useState<any[]>([]);
  const [lastAddedPanelId, setLastAddedPanelId] = useState<number | null>(null);
  const panelRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const api = useApi();
  const { items, loading: configLoading } = useNetSuiteConfigs(props.teamId);
  const metaDataOptions = getMetaDataOptions();

  const addPanel = () => {
    const newPanelId = panels.length + 1;
    setPanels([...panels, { id: newPanelId, metadataType: "" }]);
    setLastAddedPanelId(newPanelId);
  };

  const showSavedMessage = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 3000);
  };

  const fetchMappingFieldCount = useCallback(
    async (metadataType: string) => {
      if (props.selectedConfigId === 0) return { metadataType, dataLength: 0 };

      try {
        const resp = await api.messaging.get(
          `NetSuite/Integrations/Mapping/${metadataType}/${props.selectedConfigId}`
        );
        if (!resp.ok) throw new Error("Failed to fetch data");

        const json = await resp.json();
        return {
          metadataType,
          dataLength: Array.isArray(json) ? json.length : 0
        };
      } catch (error) {
        console.error(`Error fetching mapping for ${metadataType}:`, error);
        return { metadataType, dataLength: 0 };
      }
    },
    [api.messaging, props.selectedConfigId]
  );

  const fetchAllMappings = async () => {
    setPanels([]);
    setLoading(true);

    const panelPromises = metaDataOptions
      .filter((option) => option !== "")
      .map(fetchMappingFieldCount);

    const results = await Promise.all(panelPromises);

    const panelsToAdd = results
      .filter(({ dataLength }) => dataLength > 0)
      .map(({ metadataType }, index) => ({ id: index + 1, metadataType }));

    setPanels(panelsToAdd);
    setLoading(false);
  };

  useEffect(() => {
    if (props.selectedConfigId != undefined && props.selectedConfigId > 0) {
      fetchAllMappings();
    }
  }, [props.selectedConfigId]);

  useEffect(() => {
    showSavedMessage();
  }, []);

  useEffect(() => {
    if (lastAddedPanelId && panelRefs.current[lastAddedPanelId]) {
      const panelElement = panelRefs.current[lastAddedPanelId];
      if (panelElement) {
        panelElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
        setLastAddedPanelId(null);
      }
    }
  }, [lastAddedPanelId, panels]);

  return (
    <div>
      <div className="justify-between items-center inline-flex w-full">
        <div className="justify-start items-start flex">
          <SearchBar placeholderText="Search" onSearch={handleSearch} />
        </div>

        <div className="justify-end items-center gap-3 flex w-full">
          {showSaved && (
            <div className="flex items-center gap-2 text-slate-500">
              <span className="filter invert">
                {React.createElement(IconCheck, {
                  className: "text-slate-500"
                })}
              </span>
              <div className="text-slate-500 text-sm font-medium">
                All changes saved
              </div>
            </div>
          )}
          <PrimaryBlueButton
            label="Add Mapping"
            onClick={addPanel}
            className="font-medium h-full"
            prefixIcon={React.createElement(IconPlus)}
          />
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center my-4">
          <span>Loading mappings...</span>
        </div>
      )}

      <div className="mt-4">
        {panels.map((panel) => (
          <div
            key={panel.id}
            ref={(el) => {
              panelRefs.current[panel.id] = el;
            }}
          >
            <NetsuitePanel
              teamId={props.teamId!}
              metadataType={panel.metadataType}
              showSavedMessage={showSavedMessage}
              selectedConfigId={props.selectedConfigId}
              isDeleteDisabled={panels.length === 1}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
